import React, { useState, useEffect } from "react";
import { Box, Divider, Grid, Typography, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useAppSelector } from "src/app/hooks";
import { mcGeneralDiseaseService, McGeneralDiseaseItem } from "src/api/medcards/mcGeneralDiseaseService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import { parseISO } from "date-fns";
import { toLocalISO } from "src/utils/date/dateHelper";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";

interface PrevDiseaseModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | number;
  editData?: McGeneralDiseaseItem | null;
}

const PrevDiseaseModal: React.FC<PrevDiseaseModalProps> = ({
  open,
  onClose,
  patientId,
  editData,
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => (state as any).auth);

  const [operationDate, setOperationDate] = useState<Date | null>(new Date());
  const [operationUser, setOperationUser] = useState<string>("");
  const [recordDate, setRecordDate] = useState<Date | null>(new Date());
  const [disease, setDisease] = useState<string>("");
  const [diseaseName, setDiseaseName] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [note, setNote] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);

  // Kullanıcı adını otomatik olarak login durumundan al
  useEffect(() => {
    if (user?.username) {
      setOperationUser(user.username);
    }
  }, [user]);

  // Edit data varsa form'u doldur
  useEffect(() => {
    if (open && editData) {
      setDisease(editData.pid?.toString() || "");
      setDiseaseName(editData.name || "");
      setSelectedItem({
        id: editData.pid,
        name: editData.name,
        displayField: editData.name,
      });
      setNote(editData.note || editData.privatenotes || "");
      if (editData.pdate) {
        try {
          const date = typeof editData.pdate === 'string' ? parseISO(editData.pdate) : new Date(editData.pdate as any);
          setRecordDate(date);
        } catch (e) {
          setRecordDate(new Date());
        }
      }
      if (editData.cuser) {
        setOperationUser(editData.cuser);
      }
      // İşlem tarihi için pdate kullan (ctime yerine)
      if (editData.pdate) {
        try {
          const date = typeof editData.pdate === 'string' ? parseISO(editData.pdate) : new Date(editData.pdate as any);
          setOperationDate(date);
        } catch (e) {
          setOperationDate(new Date());
        }
      } else if (editData.ctime) {
        // Fallback olarak ctime kullan
        try {
          const date = typeof editData.ctime === 'string' ? parseISO(editData.ctime) : new Date(editData.ctime);
          setOperationDate(date);
        } catch (e) {
          setOperationDate(new Date());
        }
      }
    }
  }, [open, editData]);

  // Modal kapandığında state'leri temizle
  useEffect(() => {
    if (!open) {
      setDisease("");
      setDiseaseName("");
      setSelectedItem(null);
      setNote("");
      setRecordDate(new Date());
      setOperationDate(new Date());
      setSnackbarOpen(false);
    }
  }, [open]);

  const handleSave = async () => {
    if (!patientId) {
      setSnackbarMessage(t("prev-disease.errors.patient-id-not-found"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    if (!disease) {
      setSnackbarMessage(t("prev-disease.errors.disease-not-selected"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      // Tarihi dateHelper (toLocalISO) ile formatla - getLocalISO ile aynı format: yyyy-MM-dd'T'HH:mm:ss
      const formatDateForAPI = (date: Date | null): string => {
        return toLocalISO(date || new Date());
      };

      if (editData) {
        // Update işlemi
        const updateData = {
          id: editData.id,
          processdate: formatDateForAPI(recordDate),
          note: note || "",
        };
        await mcGeneralDiseaseService.updatePastDisease(updateData);
      } else {
        // Add işlemi
        const requestData = {
          ptid: Number(patientId),
          processid: Number(disease),
          processdate: formatDateForAPI(recordDate),
          note: note || "",
        };
        await mcGeneralDiseaseService.addPastDisease(requestData);
      }
      setSnackbarMessage(t("prev-disease.success.save-success"));
      setSnackbarType("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Geçirilmiş Hastalık kaydetme hatası:", error);
      const errorMessage = error?.response?.data?.error || error?.error || t("prev-disease.errors.save-error");
      setSnackbarMessage(errorMessage);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f4f7fe",
            borderBottom: "1px solid #e0e0e0",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            py: 1.5,
            px: 2.5,
            mb: 2,
          }}
        >
          <ByzMuiTypography isTitle sx={{ fontSize: 20, lineHeight: "22px", color: "#111827" }}>
            {t("prev-disease.title")}
          </ByzMuiTypography>
        </Box>

        {/* Save / Close */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={loading} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Hasta bilgisi kartı (grafiksiz) */}
        <Box sx={{ mb: 2 }}>
          <PatientDatas patientId={patientId} isCreateMedCard noMargin />
        </Box>

        {/* Form alanları */}
        <Box
          sx={{
            mt: 1,
            width: { xs: "100%", md: "60%" },
          }}
        >
          {/* Bölüm başlığı - Geçirilmiş Hastalık */}
          <Box
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
              mb: 1.5,
              px: 3,
              py: 1,
            }}
          >
            <ByzMuiTypography isTitle sx={{ fontSize: 16 }}>
              {t("prev-disease.section-title")}
            </ByzMuiTypography>
          </Box>

          <Grid container spacing={0}>
            {/* İşlem Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("prev-disease.operation-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={operationDate}
                    onChange={(val) => setOperationDate(val as Date | null)}
                    width="100%"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* İşlem Yapan Kullanıcı */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("prev-disease.operation-user")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label={t("prev-disease.operation-user")}
                    value={operationUser}
                    onChange={(e) => setOperationUser(e.target.value)}
                    fullWidth
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Tarih */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("prev-disease.date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={recordDate}
                    onChange={(val) => setRecordDate(val as Date | null)}
                    width="100%"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Hastalık */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("prev-disease.disease")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    placeholder={t("buttons.chose") || "Seçiniz"}
                    selectedItem={selectedItem}
                    value={diseaseName || ""}
                    disabled={!!editData}
                    singleSelection={true}
                    onChange={(value: any) => {
                      // Tek seçim için direkt item'ı al
                      const item = value;
                      if (item) {
                        setSelectedItem(item);
                        setDisease(String(item?.id || item?.ID || ""));
                        setDiseaseName(item?.name || item?.NAME || "");
                      } else {
                        setSelectedItem(null);
                        setDisease("");
                        setDiseaseName("");
                      }
                    }}
                    url="/reference/api/McGeneralDisease/field-past-illness"
                    method="POST"
                    postData={{
                      offId: 0,
                    }}
                    dialogTitle={t("prev-disease.dialog-title")}
                    displayField="name"
                    columns={[
                      { field: "id", headerName: "ID", width: 100 },
                      { field: "name", headerName: "Hastalık Adı", flex: 1 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Not */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("prev-disease.note")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label={t("prev-disease.note")}
                    placeholder={t("prev-disease.note") || "Not"}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarType === "success" ? t("common.success") : t("common.error")}
        message={snackbarMessage}
        type={snackbarType}
      />
    </OpenComponentDialog>
  );
};

export default PrevDiseaseModal;

